import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, ContactCTA, Footer, LazyImage } from '../../components';
import styles from './About.module.css';

const IMAGES = {
  heroBg:   'https://res.cloudinary.com/dhjzovpof/image/upload/v1773600256/5b199b3bd051a9946ee752fc2d12bc372cf9adb4_1_qemyr6.jpg',
  sector1:  'https://res.cloudinary.com/dhjzovpof/image/upload/v1773521941/DJI_0299_3_hvfkak.jpg',
  sector2:  'https://res.cloudinary.com/dhjzovpof/image/upload/v1773522707/89242676197954720c6c4308c84b388f59b97035_1_i8pgev.jpg',
  sector3:  'https://res.cloudinary.com/dhjzovpof/image/upload/v1773522780/86640ab90ff0b803f1c893437ce23e27e6a79702_2_yalaho.jpg',
};

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut', delay } },
});

// ─── Sector card ─────────────────────────────────────────────────────────────
function SectorCard({ img, title, subtitle, body }) {
  return (
    <div className={styles.sectorCard}>
      <div className={styles.sectorImgWrap}>
        <LazyImage src={img} alt={title} className={styles.sectorImg} />
        <div className={styles.sectorImgOverlay} aria-hidden="true" />
        <p className={styles.sectorImgLabel}>{title}</p>
      </div>
      <div className={styles.sectorBody}>
        <p className={styles.sectorSubtitle}>{subtitle}</p>
        <p className={styles.sectorText}>{body}</p>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function About() {
  return (
    <div className={styles.page}>
      <Navbar theme="light" cta="Contact Us" ctaTo="/contact" />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} aria-hidden="true">
          <LazyImage src={IMAGES.heroBg} alt="" />
          <div className={styles.heroGradient} />
        </div>
        <div className={styles.heroLabel}>
          <p className={styles.heroLabelText}>ABOUT TEKTRON</p>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────────────────────────── */}
      <motion.section
        className={styles.intro}
        variants={fadeUp(0)}
        initial={["visible", "active"]}
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <h2 className={styles.introHeading}>
          Engineering Excellence with Economic Purpose.
        </h2>
        <div className={styles.introBody}>
          <p>
            TektronPlus Engineering is an engineering construction company that
            offers a variety of services which includes road construction/
            rehabilitation/maintenance, road furniture: road marking, road signs,
            traffic safety devices, traffic management control devices, renewable
            energy products and production of thermoplastic road marking paint.
          </p>
          <p>
            Founded on the principles of precision and technical advancement,
            TektronPlus Engineering has emerged as a multi-disciplinary leader in
            the construction industry. Our journey began with a clear mission: to
            move beyond standard contracting and provide consultancy-driven
            engineering that solves real-world challenges.
          </p>
          <p>
            From major road rehabilitation and the production of specialized
            thermoplastic paints to the implementation of award-winning solar hybrid
            systems, our work is defined by international standards.We bring world-class innovation to local infrastructure, ensuring
            every project we touch is built to last.
          </p>
        </div>
      </motion.section>

      {/* ── MISSION & VISION ─────────────────────────────────────────────── */}
      <motion.section
        className={styles.missionSection}
        variants={fadeUp(0)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {/* Left — gradient card with stats */}
        <div className={styles.gradCard}>
          <h2 className={styles.gradCardTitle}>Our Mission and Vision</h2>
          <div className={styles.statsRow}>
            <div className={styles.stat}>
              <span className={styles.statNum}>10+</span>
              <span className={styles.statLabel}>Years Experience</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNum}>40+</span>
              <span className={styles.statLabel}>Projects Completed</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNum}>20+</span>
              <span className={styles.statLabel}>States Covered</span>
            </div>
          </div>
        </div>

        {/* Right — two gray cards */}
        <div className={styles.mvCards}>
          <div className={styles.mvCard}>
            <h3 className={styles.mvCardTitle} style={{ color: '#a61319' }}>Our Mission</h3>
            <p className={styles.mvCardBody}>
              It is our mission to provide the finest engineering service solutions
              for new constructions as well as existing structures. Through careful
              analysis, attentive customer support, and cost effective solutions,
              TektronPlus will become a stable business serving the Nigerian Public.
            </p>
          </div>
          <div className={styles.mvCard}>
            <h3 className={styles.mvCardTitle} style={{ color: '#966310' }}>Our Vision</h3>
            <p className={styles.mvCardBody}>
              To be the foremost catalyst for sustainable infrastructure, building a
              future where engineering excellence and environmental value exist in
              perfect alignment. We strive to set the global benchmark for "Green"
              urban projects, transforming how the world builds, moves, and powers
              itself.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── KEYS TO SUCCESS + OBJECTIVES ─────────────────────────────────── */}
      <motion.section
        className={styles.keySection}
        variants={fadeUp(0)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.keyPanel} style={{ background: '#fff6e8' }}>
          <h3 className={styles.keyTitle} style={{ color: '#64420a' }}>Keys To Success</h3>
          <p className={styles.keySubtitle}>
            We have identified several keys to success that will be instrumental in
            creating a sustainable business.
          </p>
          <ul className={styles.keyList}>
            <li>Offer solutions that are demanded by customers.</li>
            <li>
              Ensure all of the solutions have economic considerations built into
              the respective models.
            </li>
            <li>
              Only provide 100% customer satisfaction. All customers must have
              their expectations exceeded.
            </li>
          </ul>
        </div>
        <div className={styles.keyPanel} style={{ background: '#faebe9' }}>
          <h3 className={styles.keyTitle} style={{ color: '#530a0c' }}>Objectives</h3>
          <p className={styles.keySubtitle}>
            We have identified key objectives that it will pursue for the long-term
            success of providing seamless technology:
          </p>
          <ul className={styles.keyList}>
            <li>
              Proven cost-benefit analysis environmental approaches to construction,
              maintenance and energy consumption.
            </li>
            <li>
              To become an influential engineering construction and consultancy
              company in the country within a short period.
            </li>
          </ul>
        </div>
      </motion.section>

      {/* ── SECTORS WE SERVE ─────────────────────────────────────────────── */}
      <section className={styles.sectors}>
        <motion.div
          className={styles.sectorsHeader}
          variants={fadeUp(0)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h2 className={styles.sectorsTitle}>Sectors we serve</h2>
          <p className={styles.sectorsDesc}>
            We partner with federal and state agencies to deliver large-scale civil
            works that drive economic mobility. From primary highway construction to
            advanced traffic systems, our solutions are built to exceed national
            safety standards and endure for generations.
          </p>
        </motion.div>
        <motion.div
          className={styles.sectorCards}
          variants={fadeUp(0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <SectorCard
            img={IMAGES.sector1}
            title="Public Infrastructure &amp; Government"
            subtitle="Building the National Backbone:"
            body="We partner with federal and state agencies to deliver large-scale civil works that drive economic mobility. From highway construction to advanced traffic systems, our solutions are built to exceed national safety standards."
          />
          <SectorCard
            img={IMAGES.sector2}
            title="Commercial &amp; Industrial Hubs"
            subtitle="Powering Industrial Efficiency:"
            body="Tailored infrastructure for manufacturing plants, warehouses, and corporate campuses. We integrate high-durability road networks with smart solar hybrid systems to ensure your operations remain powered and accessible 24/7."
          />
          <SectorCard
            img={IMAGES.sector3}
            title="Residential Estates &amp; Urban Developments"
            subtitle="Creating Smarter Communities:"
            body="We help developers transform residential landscapes into premium, sustainable living spaces. Our expertise in thermoplastic marking, and solar-powered street lighting, ensures safety and modern appeal for every resident."
          />
        </motion.div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
}
